// src/components/RichText.js
import React from 'react';
import styled from 'styled-components';

// Styled components for different content types
const H1 = styled.h1`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1.2;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing['2xl']};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }
`;

const H2 = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  line-height: 1.3;
  margin-bottom: ${({ theme }) => theme.spacing.base};
  margin-top: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`;

const H3 = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  line-height: 1.4;
  margin-bottom: ${({ theme }) => theme.spacing.base};
  margin-top: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const H4 = styled.h4`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.base};
`;

const Paragraph = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.base};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.base};
  
  /* Handle inline styles from rich text */
  strong, b {
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  code {
    background: ${({ theme }) => theme.colors.surface};
    padding: 2px 6px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.9em;
    color: ${({ theme }) => theme.colors.primary};
  }
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
    transition: color ${({ theme }) => theme.transitions.fast};
    
    &:hover {
      color: ${({ theme }) => theme.colors.primaryDark};
    }
  }
`;

const UnorderedList = styled.ul`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.base};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.base};
  margin-left: ${({ theme }) => theme.spacing.xl};
  list-style-type: disc;
  
  li {
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    padding-left: ${({ theme }) => theme.spacing.xs};
    
    /* Nested lists */
    ul, ol {
      margin-top: ${({ theme }) => theme.spacing.xs};
      margin-bottom: ${({ theme }) => theme.spacing.xs};
    }
    
    /* Style for list markers */
    &::marker {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
  
  /* Handle rich text within list items */
  strong, b {
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.text};
  }
  
  code {
    background: ${({ theme }) => theme.colors.surface};
    padding: 2px 6px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.9em;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const OrderedList = styled.ol`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.base};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.base};
  margin-left: ${({ theme }) => theme.spacing.xl};
  list-style-type: decimal;
  
  li {
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    padding-left: ${({ theme }) => theme.spacing.xs};
    
    &::marker {
      color: ${({ theme }) => theme.colors.primary};
      font-weight: ${({ theme }) => theme.fontWeights.medium};
    }
  }
  
  /* Handle rich text within list items */
  strong, b {
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Blockquote = styled.blockquote`
  margin: ${({ theme }) => theme.spacing.xl} 0;
  padding: ${({ theme }) => theme.spacing.base};
  padding-left: ${({ theme }) => theme.spacing.lg};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-style: italic;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSizes.lg};
    line-height: 1.6;
  }
  
  cite {
    display: block;
    margin-top: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-style: normal;
    color: ${({ theme }) => theme.colors.textTertiary};
    
    &::before {
      content: "— ";
    }
  }
`;

const CodeBlock = styled.pre`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  padding: ${({ theme }) => theme.spacing.base};
  margin: ${({ theme }) => theme.spacing.base} 0;
  overflow-x: auto;
  
  code {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.text};
  }
`;

// Helper function to process inline formatting
function processInlineFormatting(text) {
  if (!text || typeof text !== 'string') return text;
  
  let formatted = text;
  
  // FIRST: Convert newlines to <br> tags
  // Handle both \\n (escaped) and \n (actual newlines)
  formatted = formatted.replace(/\\n/g, '<br>');
  formatted = formatted.replace(/\n/g, '<br>');
  
  // Bold: **text** or __text__
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>');
  
  // Italic: *text* or _text_ (but not within bold)
  formatted = formatted.replace(/(?<!\*)\*(?!\*)([^*]+)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  formatted = formatted.replace(/(?<!_)_(?!_)([^_]+)(?<!_)_(?!_)/g, '<em>$1</em>');
  
  // Inline code: `code`
  formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // Links: [text](url)
  formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  
  return formatted;
}

const RichText = ({ content, type = 'body' }) => {
  // Handle empty content
  if (!content) return null;
  
  // Process content based on type
  switch (type) {
    case 'h1':
    case 'heading1':
      return <H1>{content}</H1>;
      
    case 'h2':
    case 'heading2':
      return <H2>{content}</H2>;
      
    case 'h3':
    case 'heading3':
      return <H3>{content}</H3>;
      
    case 'h4':
    case 'heading4':
      return <H4>{content}</H4>;
      
    case 'list':
    case 'bullet_list':
    case 'Bullet List': // Handle the exact case from your CMS
    case 'unordered_list':
      // Clean up the content first - remove any HTML tags if Rich Text was used
      let cleanContent = content;
      if (typeof content === 'string') {
        // Strip HTML tags but keep the text content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        cleanContent = tempDiv.textContent || tempDiv.innerText || content;
      }
      
      // Handle semicolon-separated list items (your primary format)
      if (typeof cleanContent === 'string' && cleanContent.includes(';')) {
        const items = cleanContent.split(';').map(item => item.trim()).filter(Boolean);
        return (
          <UnorderedList>
            {items.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ 
                __html: processInlineFormatting(item)
              }} />
            ))}
          </UnorderedList>
        );
      }
      // Handle newline-separated list items
      else if (typeof cleanContent === 'string' && cleanContent.includes('\n')) {
        const items = cleanContent.split('\n').map(item => item.trim()).filter(Boolean);
        return (
          <UnorderedList>
            {items.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ 
                __html: processInlineFormatting(item)
              }} />
            ))}
          </UnorderedList>
        );
      }
      // Handle array of items
      else if (Array.isArray(content)) {
        return (
          <UnorderedList>
            {content.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ 
                __html: processInlineFormatting(item)
              }} />
            ))}
          </UnorderedList>
        );
      }
      // Single item
      else if (cleanContent) {
        return (
          <UnorderedList>
            <li dangerouslySetInnerHTML={{ 
              __html: processInlineFormatting(cleanContent)
            }} />
          </UnorderedList>
        );
      }
      // Empty list
      return null;
      
    case 'numbered_list':
    case 'ordered_list':
      // Handle semicolon-separated list items
      if (typeof content === 'string' && content.includes(';')) {
        const items = content.split(';').map(item => item.trim()).filter(Boolean);
        return (
          <OrderedList>
            {items.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ 
                __html: processInlineFormatting(item)
              }} />
            ))}
          </OrderedList>
        );
      }
      // Handle array of items
      else if (Array.isArray(content)) {
        return (
          <OrderedList>
            {content.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ 
                __html: processInlineFormatting(item)
              }} />
            ))}
          </OrderedList>
        );
      }
      // Single item
      else {
        return (
          <OrderedList>
            <li dangerouslySetInnerHTML={{ 
              __html: processInlineFormatting(content)
            }} />
          </OrderedList>
        );
      }
      
    case 'quote':
    case 'blockquote':
      return (
        <Blockquote>
          <p dangerouslySetInnerHTML={{ 
            __html: processInlineFormatting(content)
          }} />
        </Blockquote>
      );
      
      case 'code':
      case 'code_block':
        // Handle both object format {code, lang} and string format
        let codeContent = content;
        let language = 'javascript'; // default language
        
        // If content is an object with code property
        if (typeof content === 'object' && content !== null) {
          codeContent = content.code || content.content || '';
          language = content.lang || content.language || 'javascript';
        }
        
        return (
          <CodeBlock>
            <code className={`language-${language}`}>
              {codeContent}
            </code>
          </CodeBlock>
        );
      
    case 'body':
    case 'paragraph':
    case 'text':
    default:
      // Check if content has HTML tags
      if (typeof content === 'string' && content.includes('<') && content.includes('>')) {
        return (
          <Paragraph dangerouslySetInnerHTML={{ 
            __html: content
          }} />
        );
      }
      // Process inline formatting for plain text
      return (
        <Paragraph dangerouslySetInnerHTML={{ 
          __html: processInlineFormatting(content)
        }} />
      );
  }
};

export default RichText;